import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { NinpoCoreContextValue } from '../app/hooks/useNinpoCore';
import BackendStatusBanner from '../app/components/BackendStatusBanner';
import ImpersonationBanner from '../app/components/ImpersonationBanner';
import AnnouncementBanner from '../app/components/AnnouncementBanner';
import ToastStack from '../app/components/ToastStack';
import Header from '../app/components/Header';
import WrongAppScreen from '../features/auth/components/WrongAppScreen';
const LoginPage = React.lazy(() => import('../features/auth/pages/LoginPage'));
import { getWrongAppInfo, isRoleAllowedOnSurface } from '../lib/deploySurface';
import type { AuthLoginResponse } from '../features/auth/types/auth.types';
import { analytics } from '../app/services/analyticsService';
import { normalizeRole, hasAnyRole, APP_ROLES } from '../features/auth/rbac';
import { authApi } from '../features/auth/api/auth.api';
import { FEATURE_ROUTES } from '../app/routeConstants';
import { useFocusTrap } from '../lib/useFocusTrap';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export interface AppShellLayoutProps {
  core: NinpoCoreContextValue;
  surface: ReturnType<typeof import('../lib/deploySurface').getDeploySurface>;
  requirePrivilegedLogin?: boolean;
  children: ReactNode;
  additionalOverlays?: ReactNode;
  onOpenSupport?: () => void;
  /** Parent shells bind this so route context can open the login modal. */
  openLoginRef?: React.MutableRefObject<(() => void) | null>;
  topActions?: React.ReactNode;
}

export default function AppShellLayout({
  core,
  surface,
  requirePrivilegedLogin,
  children,
  additionalOverlays,
  onOpenSupport = () => {},
  openLoginRef,
  topActions,
}: AppShellLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoginViewOpen, setIsLoginViewOpen] = useState(false);
  const [wrongAppAfterLogin, setWrongAppAfterLogin] = useState<ReturnType<typeof getWrongAppInfo>>(null);
  const wrongApp = core.currentUser ? getWrongAppInfo(core.currentUser, surface) : null;
  const loginPanelRef = useRef<HTMLDivElement | null>(null);

  const isMcpAppPath = location.pathname.startsWith('/mcpApp');

  useBodyScrollLock(isLoginViewOpen && !wrongApp);
  useFocusTrap(isLoginViewOpen && !wrongApp, loginPanelRef, () => setIsLoginViewOpen(false));

  useEffect(() => {
    if (!openLoginRef) return;
    openLoginRef.current = () => setIsLoginViewOpen(true);
    return () => {
      openLoginRef.current = null;
    };
  }, [openLoginRef]);

  // Analytics tracking
  useEffect(() => {
    const currentUserId = core.currentUser?._id;
    analytics.trackPageView(location.pathname, currentUserId);
  }, [location.pathname, core.currentUser]);

  // Auto-trigger login modal on ?login=1 query param and clean URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('login') === '1') {
      setIsLoginViewOpen(true);
      const cleanParams = new URLSearchParams(location.search);
      cleanParams.delete('login');
      const searchStr = cleanParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: searchStr ? `?${searchStr}` : '',
        },
        { replace: true }
      );
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (core.currentUser) {
      analytics.setUser(
        core.currentUser._id,
        core.currentUser.email,
        core.currentUser.tier
      );
      analytics.trackUserAction('user_logged_in', {
        tier: core.currentUser.tier,
        role: core.currentUser.role
      });
    } else {
      analytics.clearUser();
    }
  }, [core.currentUser]);

  const handleLoginSuccess = async (loginResponse: AuthLoginResponse) => {
    const userCandidate = loginResponse?.data?.user;
    if (!isRoleAllowedOnSurface(userCandidate, surface)) {
      await authApi.logout().catch(() => {});
      setWrongAppAfterLogin(getWrongAppInfo(userCandidate, surface));
      return;
    }
    const applied = core.applyLoginSession
      ? await core.applyLoginSession(loginResponse)
      : false;
    if (!applied) {
      return;
    }
    const isStaff = hasAnyRole(userCandidate, [APP_ROLES.admin, APP_ROLES.owner]);
    const postLoginFetches: Promise<unknown>[] = [core.fetchOrders?.().catch(() => {})];
    if (!isStaff && (hasAnyRole(userCandidate, [APP_ROLES.customer]) || hasAnyRole(userCandidate, [APP_ROLES.driver]))) {
      postLoginFetches.push(core.fetchProducts?.().catch(() => {}));
    }
    if (postLoginFetches.length) {
      await Promise.all(postLoginFetches);
    }
    setIsLoginViewOpen(false);
    setWrongAppAfterLogin(null);
    if (
      isStaff &&
      surface === 'website' &&
      !location.pathname.startsWith('/admin') &&
      !location.pathname.startsWith('/owner')
    ) {
      navigate(
        hasAnyRole(userCandidate, [APP_ROLES.owner])
          ? FEATURE_ROUTES.admin.productQueue
          : FEATURE_ROUTES.admin.home,
        { replace: true }
      );
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith('/resetPassword')) {
      setIsLoginViewOpen(false);
    }
  }, [location.pathname]);

  if (isMcpAppPath) {
    return <>{children}</>;
  }

  if (core.isBootstrapping) {
    return (
      <div className="min-h-screen bg-ninpo-black text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-16 w-16 rounded-full border-4 border-white/20 border-t-white animate-spin" />
          <p className="text-xs uppercase tracking-[0.4em] text-white/60">Loading storefront</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ninpo-black text-white flex flex-col relative overflow-x-hidden font-sans">
      <BackendStatusBanner isOnline={core.isBackendOnline} onReconnect={core.syncWithBackend} />
      <ImpersonationBanner isLoggedIn={!!core.currentUser} />
      <AnnouncementBanner
        announcementText={core.settings?.announcementBannerText}
        announcementBadge={core.settings?.announcementBannerBadge}
      />
      <ToastStack toasts={core.toasts} />
      
      <Header
        currentUserRole={core.currentUser?.role}
        isLoggedIn={!!core.currentUser}
        onLogin={() => setIsLoginViewOpen(true)}
        onLogout={() => core.logout?.()}
        onOpenSupport={onOpenSupport}
      />

      <main className="flex-1 px-6 py-10 max-w-[1600px] w-full mx-auto relative">
        {topActions}
        {wrongApp ? (
          <WrongAppScreen
            info={wrongApp}
            onSignOut={() => {
              void core.logout?.();
            }}
          />
        ) : (
          children
        )}
      </main>

      {additionalOverlays}

      {isLoginViewOpen && !wrongApp && (
        <div
          className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/90 px-6"
          role="dialog"
          aria-modal="true"
          aria-label="Sign in"
        >
          <div ref={loginPanelRef} tabIndex={-1} className="w-full max-w-md outline-none">
            {wrongAppAfterLogin ? (
              <WrongAppScreen
                info={wrongAppAfterLogin}
                onSignOut={() => {
                  setWrongAppAfterLogin(null);
                  setIsLoginViewOpen(false);
                  void core.logout?.();
                }}
              />
            ) : (
              <React.Suspense
                fallback={
                  <div className="flex h-64 items-center justify-center rounded-2xl border border-white/10 bg-slate-900/90 p-8 text-sm text-slate-400">
                    Loading sign in…
                  </div>
                }
              >
                <LoginPage
                  onSuccess={handleLoginSuccess}
                  onCancel={() => setIsLoginViewOpen(false)}
                  requireMfa={requirePrivilegedLogin}
                />
              </React.Suspense>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
